import { useState } from 'react';
import BrandComparativeFilterModal from '@/components/Modals/PriceComparativeChartModal/BrandComparativeFilterModal';
import ItemComparativeFilterModal from '@/components/Modals/PriceComparativeChartModal/ItemComparativeFilterModal';
import LocationComparativeFilterModal from '@/components/Modals/PriceComparativeChartModal/LocationComparativeFilterModal';
import { ILocationList } from '@/pages/preco-regiao';
import {
  IBrandsPricingComparative,
  IPricingComparative,
  IChartDateFilter,
  ChartDateFilterLabels,
} from '@/types/api/pricingsComparativeRegion';
import { dateFilters } from '@/utils/components/priceComparativeChartModal';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Chip, MenuItem, Select, Stack } from '@mui/material';
import { FreeMode } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

interface IRefineFilterProps {
  onChangeDateFilter: (value: number) => void;
  onChangeItemFilter: (id: number) => void;
  onChangeBrandFilter: (id: number | null) => void;
  onChangeLocationFilter: (id: number) => void;
  items: Array<IPricingComparative & { selected: boolean }>;
  brands: Array<IBrandsPricingComparative & { selected: boolean }>;
  locations: Array<ILocationList & { selected: boolean }>;
}
interface IDateSelectProps {
  handleChange: (value: number) => void;
}

interface IFilter {
  name: string;
  handleClick?: () => void;
  type: 'item' | 'location' | 'brand' | 'date';
}

const DateSelect = ({ handleChange }: IDateSelectProps) => {
  const initialFilter = dateFilters.find(
    filter => filter.name === ChartDateFilterLabels.ThirtyDays
  ) as IChartDateFilter;

  return (
    <Select
      className="swiper-no-swiping"
      defaultValue={initialFilter.valueInSeconds}
      onChange={({ target: { value } }) => handleChange(Number(value))}
      IconComponent={KeyboardArrowDown}
      MenuProps={{
        sx: {
          '& .MuiMenu-paper': {
            boxShadow: '1px 5px 10px rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            mt: 1,
          },
          '& .MuiMenuItem-root': {
            fontSize: '14px',
            p: 2,
          },
        },
        MenuListProps: {
          disablePadding: true,
        },
      }}
      sx={{
        color: 'text.secondary',
        fontSize: '13px',
        padding: 0,
        borderRadius: '20px',
        '& .MuiSelect-select': {
          py: 1,
        },
        '& .MuiSelect-icon': {
          fontSize: 20,
        },
        '&:hover': {
          '.MuiSelect-select, .MuiSelect-icon': {
            borderRadius: '20px',
            color: 'primary.contrastText',
            backgroundColor: 'primary.main',
          },
          '&& fieldset': {
            border: '1px solid #bdbdbd',
          },
        },
        '&.Mui-focused': {
          '&& fieldset': {
            border: '1px solid #bdbdbd',
          },
        },
      }}
    >
      {dateFilters.map(dateFilter => (
        <MenuItem
          key={dateFilter.valueInSeconds}
          value={dateFilter.valueInSeconds}
        >
          {dateFilter.name}
        </MenuItem>
      ))}
    </Select>
  );
};

const RefineFilter = ({
  items,
  brands,
  locations,
  onChangeBrandFilter,
  onChangeDateFilter,
  onChangeItemFilter,
  onChangeLocationFilter,
}: IRefineFilterProps) => {
  const [openFilterItem, setOpenFilterItem] = useState(false);
  const [openFilterBrand, setOpenFilterBrand] = useState(false);
  const [openFilterLocation, setOpenFilterLocation] = useState(false);
  const currentItem = items.find(item => item.selected);
  const currentBrand = brands.find(brand => brand.selected);
  const currentLocation = locations.find(location => location.selected);

  const filters: IFilter[] = [
    {
      name: currentItem?.name || '',
      handleClick: () => setOpenFilterItem(true),
      type: 'item',
    },
    {
      name: currentLocation?.name || '',
      handleClick: () => setOpenFilterLocation(true),
      type: 'location',
    },
    {
      name: currentBrand?.name ? currentBrand.name : 'Todas as marcas',
      handleClick: () => setOpenFilterBrand(true),
      type: 'brand',
    },
    { name: 'Date filter input', type: 'date' },
  ];

  const handleDateFilter = (value: number) => onChangeDateFilter(value);

  const hasBrands = brands.some(
    brand => Boolean(brand.id) && Boolean(brand.name)
  );

  return (
    <>
      <Stack
        direction="row"
        sx={theme => ({
          width: '100%',
          maxWidth: theme.breakpoints.values.xl,
        })}
      >
        <Swiper
          slidesPerView="auto"
          spaceBetween={8}
          freeMode={true}
          grabCursor={true}
          pagination={{
            clickable: true,
          }}
          modules={[FreeMode]}
          style={{
            textAlign: 'left',
            width: '100%',
            padding: '8px 0',
            paddingRight: 'calc(100% - 80%)',
          }}
        >
          {filters.map(({ type, handleClick, name }) => (
            <Box key={type}>
              {type === 'date' && (
                <SwiperSlide key={type} className="swiperSlide-auto">
                  <DateSelect handleChange={handleDateFilter} />
                </SwiperSlide>
              )}

              {type === 'brand' && hasBrands && (
                <SwiperSlide key={type} className="swiperSlide-auto">
                  <Chip
                    label={name}
                    variant="outlined"
                    icon={<KeyboardArrowDown fontSize="small" />}
                    clickable
                    onClick={handleClick}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row-reverse',
                      p: 2,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                      '& .MuiChip-label': {
                        p: 0,
                      },
                    }}
                  />
                </SwiperSlide>
              )}

              {type !== 'date' && type !== 'brand' && (
                <SwiperSlide key={type} className="swiperSlide-auto">
                  <Chip
                    label={name}
                    variant="outlined"
                    icon={<KeyboardArrowDown fontSize="small" />}
                    clickable
                    onClick={handleClick}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row-reverse',
                      p: 2,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                      '& .MuiChip-label': {
                        p: 0,
                      },
                    }}
                  />
                </SwiperSlide>
              )}
            </Box>
          ))}
        </Swiper>
      </Stack>

      {openFilterItem && (
        <ItemComparativeFilterModal
          isOpen={openFilterItem}
          handleClose={() => setOpenFilterItem(false)}
          onChangeItem={onChangeItemFilter}
          items={items}
        />
      )}

      {openFilterBrand && (
        <BrandComparativeFilterModal
          isOpen={openFilterBrand}
          handleClose={() => setOpenFilterBrand(false)}
          onChangeBrand={onChangeBrandFilter}
          brands={brands}
        />
      )}

      {openFilterLocation && (
        <LocationComparativeFilterModal
          isOpen={openFilterLocation}
          handleClose={() => setOpenFilterLocation(false)}
          onChangeLocation={onChangeLocationFilter}
          locations={locations}
        />
      )}
    </>
  );
};

export default RefineFilter;
export type { IRefineFilterProps };
